import { useCallback, useMemo } from 'react';
import { Paths, createPath } from '../../../../app/router/app.router';
import { useKioskTable } from '../hooks/use-kiosk-table.hook';
import { useQueryKioskStates } from '../kiosk-status.queries';
import {
  InfiniteTable,
  InfiniteScrollProps
} from '../../../../ui/components/pure/list-table-wrappers/infinite-table.component';
import { KioskState } from '../types/kiosk.types';
import { Button, Grid } from '@mui/material';
import { Refresh } from '@mui/icons-material';
import { useQueryClient } from 'react-query';
import { QueryKeys } from '../../../../core/queries/query-keys.constants';
import { KnownSortOrder } from '@groundfloor-platform/mgmt-service-g-sdk';
import { useKioskProjectMapping } from '../hooks/use-kiosk-project-mapping.hook';

interface KioskListProps {
  projectID: string;
}

export function KioskList({ projectID }: KioskListProps) {
  const queryClient = useQueryClient();
  const {
    data: kioskStates,
    isLoading,
    isFetching,
    error,
    refetch,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage
  } = useQueryKioskStates(projectID);

  const {
    kioskIdToHubName,
    kioskIdToProjectName,
    hasRequiredData: hasRequiredMappingData,
    isLoading: mappingLoading
  } = useKioskProjectMapping(projectID);

  const infiniteScroll: InfiniteScrollProps = useMemo(
    () => ({
      hasNextPage: hasNextPage || false,
      onScrollToBottom: () => {
        fetchNextPage();
      },
      isFetchingNextPage: isFetchingNextPage,
      fetchNextPage: fetchNextPage
    }),
    [hasNextPage, fetchNextPage, isFetchingNextPage]
  );

  const tb = useKioskTable(kioskIdToHubName, kioskIdToProjectName);

  // Map each row to its detail page URL
  const mapRowURL = useCallback(
    (row: KioskState) => {
      return createPath(Paths.kioskConnectionStatusDetail, {
        projectID,
        kioskID: row.id
      });
    },
    [projectID]
  );
  tb.config({ mapRowURL });

  const handleRefresh = useCallback(() => {
    queryClient.invalidateQueries([QueryKeys.REPORTS, 'KIOSK_STATUS']);
    refetch();
  }, [queryClient, refetch]);

  const sortData = useMemo(
    () => ({
      sortBy: 'lastUpdated',
      sortOrder: KnownSortOrder.Desc
    }),
    []
  );

  const hasError = !!error;
  // Only show loading spinner on initial load when we don't have data yet
  // Once we have data, background refetches won't block the UI
  const isInitialLoading = isLoading && !kioskStates;
  const isLoadingAny = isInitialLoading || (mappingLoading && !hasRequiredMappingData);

  return (
    <Grid container spacing={2}>
      <Grid size={12}>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={handleRefresh}
          disabled={isLoadingAny || isFetching || !hasRequiredMappingData}
          style={{ marginBottom: '16px', backgroundColor: 'white' }}
        >
          Refresh
        </Button>
      </Grid>
      <Grid size={12}>
        <InfiniteTable
          uniqueKey="id"
          onSort={() => {}}
          sortData={sortData}
          table={tb}
          pages={kioskStates?.pages || []}
          infiniteScroll={infiniteScroll}
          isLoading={isLoadingAny}
          isPreviousData={isFetching && hasRequiredMappingData}
          hasError={hasError}
          errorMessage={error ? 'Failed to load kiosk states' : undefined}
        />
      </Grid>
    </Grid>
  );
}
